import React, { useState, useRef, useEffect } from 'react';

import { ReactComponent as ArrowIcon } from '../../assets/icons/down-arrow.svg';
import { ReactComponent as SearchIcon } from '../../assets/icons/search-icon.svg';

type Option = {
  value: string | number;
  label: string;
};

interface SelectFieldProperties {
  inputLabel?: string;
  options: Option[]; // Array of objects with value and label
  placeholder?: string;
  selectedValue: string | number;
  name: string;
  onChangeValue: (name: string, value: any) => void;
  valueRequired?: boolean;
  error?: boolean;
  errorMessage?: string;
  svgIcon?: JSX.Element;
  readOnly?: boolean;
  fontSize?: 'small' | 'medium' | 'large';
  disabled?: boolean;
  disabledAutoSorting?: boolean; // Optional prop to disable auto-sorting of options
  searchLabel?: boolean;
}

/**
 * Select Box filed function component
 */
function SelectField({
  inputLabel,
  options,
  name,
  placeholder,
  selectedValue,
  onChangeValue,
  error,
  errorMessage,
  valueRequired,
  svgIcon,
  readOnly,
  disabled,
  fontSize = 'large',
  disabledAutoSorting = false, // Default to false
  searchLabel,
}: SelectFieldProperties) {
  const reference = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState<string>('close');
  const [style, setStyle] = useState<any>({});
  const [searchTerm, setSearchTerm] = useState<string>('');
  const searchInputReference = useRef<HTMLInputElement>(null);
  /**
   * Handle the value change function
   */
  // const handleValueChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
  const handleValueChange = (changeValue: any) => {
    if (readOnly) {
      return;
    }

    onChangeValue(name, changeValue);
    setOpen('close');
    setSearchTerm('');
  };

  /**
   * Handle the close th drop down function
   * @param event
   */
  const handleClickOutside = (event: MouseEvent) => {
    if (reference.current && !reference.current.contains(event.target as Node)) {
      setOpen('close');
      setSearchTerm('');
    }
  };

  /**
   * Get the dropdown position
   */
  const openDropDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (disabled || readOnly) return;

    // 🔁 Toggle
    if (open === 'open') {
      setOpen('close');
      setSearchTerm('');
      return;
    }

    const { offsetHeight, offsetWidth } = event.currentTarget;

    if (window.innerHeight > event.clientY + 300) {
      setStyle({
        top: `${inputLabel ? offsetHeight + 30 : offsetHeight}px`,
        width: `${offsetWidth}px`,
      });
    } else {
      setStyle({
        bottom: `${offsetHeight}px`,
        width: `${offsetWidth}px`,
        borderBottomColor: '#6c757d',
        borderTopColor: '#e3e8f1',
        borderRadius: '8px 8px 0 0',
      });
    }

    setOpen('open');
  };

  const sortedOptions = React.useMemo(() => {
    if (disabledAutoSorting) return options; // Sorting disabled → return as-is
    if (!options || options.length === 0) return [];

    // 1️⃣ Find optional “All Station” entry (by value or label)
    const allStationIndex = options.findIndex(
      (opt) => opt.value === 'all' || opt.label.toLowerCase() === 'all station',
    );

    let allStationOption: Option | null = null;
    const restOptions = [...options];

    // 2️⃣ Remove it from the list temporarily
    if (allStationIndex !== -1) {
      allStationOption = restOptions[allStationIndex];
      restOptions.splice(allStationIndex, 1);
    }

    // 3️⃣ Sort others alphabetically
    restOptions.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));

    // 4️⃣ Put “All Station” on top only when it exists
    return allStationOption ? [allStationOption, ...restOptions] : restOptions;
  }, [options, disabledAutoSorting]);

  const filteredOptions = React.useMemo(() => {
    if (!searchTerm) return sortedOptions;
    return sortedOptions.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [sortedOptions, searchTerm]);

  useEffect(() => {
    if (open === 'open' && searchLabel && searchInputReference.current) {
      searchInputReference.current.focus();
    }
  }, [open, searchLabel]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className={`custom-select-wrap ${fontSize}`} ref={reference}>
      {inputLabel && (
        <p className="input-label">
          {inputLabel}
          {valueRequired && <span>*</span>}
        </p>
      )}
      <div
        style={{ cursor: disabled ? 'not-allowed' : 'text' }}
        className={`custom-select-container ${error && 'error'} ${open}`}
        onClick={openDropDown}
        role="presentation">
        {svgIcon ? <div className="front-icon">{svgIcon}</div> : <div />}
        <div className="view-value">
          {(() => {
            const selectedOption = options.find((option) => option.value === selectedValue);
            let valueToDisplay = <p className="placeholder">{placeholder}</p>;
            if (selectedOption) {
              valueToDisplay = <p className="select-value">{selectedOption.label}</p>;
            }
            return valueToDisplay;
          })()}
        </div>
        <span className={`custom-icon ${open}`}>
          <ArrowIcon />
        </span>
      </div>
      {error && <p className="custom-input-error-message">{errorMessage}</p>}
      <div className={`dropdown-items-list ${open}`} ref={reference} style={style}>
        {searchLabel && (
          <div className="search-filter-input">
            <div className="custom-input-wrap medium">
              <div className="custom-input-container">
                <span>
                  <SearchIcon />
                </span>
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  ref={searchInputReference}
                />
              </div>
            </div>
          </div>
        )}
        {!disabled &&
          filteredOptions.map((option) => (
            <div
              className="items"
              key={option?.value}
              onClick={() => handleValueChange(option.value)}
              role="presentation">
              {option?.label}
            </div>
          ))}
        {filteredOptions.length === 0 && <div className="empty-items">No Data</div>}
      </div>
    </div>
  );
}

SelectField.defaultProps = {
  inputLabel: '',
  placeholder: '',
  valueRequired: false,
  error: false,
  errorMessage: '',
  svgIcon: null,
  readOnly: false,
  fontSize: 'large',
  disabled: false,
  disabledAutoSorting: false, // Default to false
  searchLabel: false,
};

export default SelectField;
